import { resolvePath, type RouterNavigateOptions, type To } from 'react-router-dom';
import {
  isChatSpaceSwitch,
  resolveAppSpace,
  resolveRouteTransitionDirection,
  shouldAnimateRouteChange,
} from '@/features/chat/chat-space-navigation';

type NavigableRouter = {
  navigate: {
    (to: number): Promise<void>;
    (to: To | null, opts?: RouterNavigateOptions): Promise<void>;
  };
  state: { location: { pathname: string; search: string } };
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Crossfades the page when the visible screen changes. React Router only starts a
 * view transition when the navigation asks for one, so every push goes through here.
 * A replace (conversation id sync, filter typing) never animates.
 */
export function installFluidRouteTransitions(router: NavigableRouter) {
  const navigate = router.navigate.bind(router);

  router.navigate = ((to: To | number | null, opts?: RouterNavigateOptions) => {
    if (typeof to === 'number') {
      return navigate(to);
    }
    if (to == null || prefersReducedMotion()) {
      return navigate(to, opts);
    }

    const from = router.state.location;
    const resolved = resolvePath(to, from.pathname);
    if (!shouldAnimateRouteChange(from, resolved, opts)) {
      delete document.documentElement.dataset.routeSwitch;
      return navigate(to, opts);
    }

    document.documentElement.dataset.routeDirection = resolveRouteTransitionDirection(
      resolveAppSpace(from.pathname),
      resolveAppSpace(resolved.pathname),
    );
    if (isChatSpaceSwitch(from.pathname, resolved.pathname)) {
      document.documentElement.dataset.routeSwitch = 'space';
    } else {
      delete document.documentElement.dataset.routeSwitch;
    }
    return navigate(to, { ...opts, viewTransition: true });
  }) as NavigableRouter['navigate'];
}
